import React, { useEffect, useMemo, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AdminSidebar from '../../components/AdminSidebar';
import { API_BASE_URL } from '../../config';

const BLOG_MENU_ITEMS = [
  { key: 'categories', label: 'Kategoriler' },
  { key: 'new-category', label: 'Yeni Kategori' },
  { key: 'posts', label: 'Yazılar' },
  { key: 'archive', label: 'Arşiv' },
  { key: 'new-post', label: 'Yeni Yazı' },
  { key: 'reorder', label: 'Sıralama' }
];

const BLOG_STATUS_OPTIONS = [
  { value: 'draft', label: 'Taslak' },
  { value: 'published', label: 'Yayında' },
  { value: 'archived', label: 'Arşiv' }
];

const MENU_ITEMS = [
  { key: 'categories', label: 'Kategoriler' },
  { key: 'new-category', label: 'Yeni Kategori' },
  { key: 'posts', label: 'Yazılar' },
  { key: 'new-post', label: 'Yeni Yazı' },
  { key: 'reorder', label: 'Sıralama' }
];

const CONTENT_TYPE_OPTIONS = [
  { value: 'blog', label: 'Blog' },
  { value: 'guide', label: 'Rehber' },
  { value: 'how_it_works', label: 'Nasıl Çalışır' },
  { value: 'academy', label: 'Akademi' }
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Taslak' },
  { value: 'published', label: 'Yayında' },
  { value: 'archived', label: 'Arşiv' }
];

const BLOG_MENU_ITEMS_V2 = [
  { key: 'categories', label: 'Kategoriler' },
  { key: 'new-category', label: 'Yeni Kategori' },
  { key: 'posts', label: 'Yazılar' },
  { key: 'featured', label: 'Öne Çıkanlar' },
  { key: 'archive', label: 'Arşiv' },
  { key: 'new-post', label: 'Yeni Yazı' }
];

const EMPTY_CATEGORY_FORM = {
  name: '',
  slug: '',
  description: '',
  seoTitle: '',
  seoDescription: '',
  introContent: '',
  priority: 0,
  sortOrder: 0,
  isPublished: false,
  isActive: true
};

const EMPTY_POST_FORM = {
  title: '',
  slug: '',
  contentType: 'blog',
  categoryId: '',
  coverImageUrl: '',
  coverImageAlt: '',
  coverImageCaption: '',
  excerpt: '',
  htmlContent: '',
  status: 'draft',
  isFeatured: false,
  sortOrder: 0,
  seoMetaTitle: '',
  seoMetaDescription: '',
  canonicalUrl: '',
  ogImageUrl: '',
  jsonLdType: 'Article',
  isIndexed: true
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image'],
    ['clean']
  ]
};

function getAdminHeaders() {
  const adminToken = localStorage.getItem('adminToken');
  const authToken = localStorage.getItem('authToken');
  const token = adminToken || authToken;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
    /*
    admin_blog_post_restore_failed: 'Yazı arşivden çıkarılamadı.',
    admin_blog_post_permanent_delete_failed: 'Yazı kalıcı olarak silinemedi.',
    blog_post_not_archived: 'Kalıcı silme için yazı önce arşivde olmalı.'
    */
  };
}

function slugifyClient(value) {
  return String(value || '')
    .trim()
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function stripHtmlPreview(value) {
  return String(value || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function resolveBlogImageUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.startsWith('/uploads/') || raw.startsWith('/api/uploads/')) return `${API_BASE_URL}${raw}`;
  if (raw.startsWith('uploads/')) return `${API_BASE_URL}/${raw}`;
  return raw;
}

function isPostSeoReady(post) {
  const description = String(post?.seoMetaDescription || '').trim();
  return Boolean(
    String(post?.title || '').trim() &&
    String(post?.slug || '').trim() &&
    String(post?.htmlContent || '').trim() &&
    String(post?.seoMetaTitle || '').trim() &&
    description &&
    description.length <= 160 &&
    String(post?.coverImageAlt || '').trim()
  );
}

function getStatusTone(status) {
  if (status === 'published') {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300';
  }
  if (status === 'archived') {
    return 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300';
  }
  return 'bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300';
}

function mapCategoryToForm(category) {
  return {
    name: category?.name || '',
    slug: category?.slug || '',
    description: category?.description || '',
    seoTitle: category?.seoTitle || '',
    seoDescription: category?.seoDescription || '',
    introContent: category?.introContent || '',
    priority: Number(category?.priority || 0),
    sortOrder: Number(category?.sortOrder || 0),
    isPublished: category?.isPublished === true,
    isActive: category?.isActive !== false
  };
}

function mapPostToForm(post) {
  return {
    title: post?.title || '',
    slug: post?.slug || '',
    contentType: post?.contentType || 'blog',
    categoryId: post?.categoryId ? String(post.categoryId) : '',
    coverImageUrl: post?.coverImageUrl || '',
    coverImageAlt: post?.coverImageAlt || '',
    coverImageCaption: post?.coverImageCaption || '',
    excerpt: post?.excerpt || '',
    htmlContent: post?.htmlContent || '',
    status: post?.status || 'draft',
    isFeatured: Boolean(post?.isFeatured),
    sortOrder: Number(post?.sortOrder || 0),
    seoMetaTitle: post?.seoMetaTitle || '',
    seoMetaDescription: post?.seoMetaDescription || '',
    canonicalUrl: post?.canonicalUrl || '',
    ogImageUrl: post?.ogImageUrl || '',
    jsonLdType: post?.jsonLdType || 'Article',
    isIndexed: post?.isIndexed !== false
  };
}

function formatApiError(errorCode, fallback) {
  const map = {
    name_required: 'Kategori adı zorunlu.',
    slug_required: 'Slug zorunlu.',
    forbidden: 'Bu işlem için yetkiniz yok.',
    title_required: 'Yazı başlığı zorunlu.',
    html_content_required: 'Yazı içeriği zorunlu.',
    seo_meta_title_required: 'SEO meta title zorunlu.',
    seo_meta_description_required: 'SEO meta description zorunlu.',
    seo_meta_description_too_long: 'SEO meta description 160 karakteri geçemez.',
    cover_image_alt_required: 'Kapak görseli alt metni zorunlu.',
    invalid_category_id: 'Geçerli bir kategori seçmelisin.',
    slug_not_unique: 'Bu slug zaten kullanılıyor.',
    admin_blog_category_create_failed: 'Kategori oluşturulamadı.',
    admin_blog_category_update_failed: 'Kategori güncellenemedi.',
    admin_blog_category_delete_failed: 'Kategori pasifleştirilemedi.',
    admin_blog_post_create_failed: 'Yazı oluşturulamadı.',
    admin_blog_post_update_failed: 'Yazı güncellenemedi.',
    admin_blog_post_delete_failed: 'Yazı arşivlenemedi.',
    admin_blog_post_publish_failed: 'Yazı yayınlanamadı.'
  };
  map.admin_blog_post_restore_failed = 'Yazı arşivden çıkarılamadı.';
  map.admin_blog_post_permanent_delete_failed = 'Yazı kalıcı olarak silinemedi.';
  map.blog_post_not_archived = 'Kalıcı silme için yazı önce arşivde olmalı.';
  return map[errorCode] || fallback;
}

function CategoryStatusBadge({ category }) {
  if (category?.isDeleted) {
    return (
      <span className="rounded-full bg-red-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-red-700 dark:bg-red-950/30 dark:text-red-300">
        Pasif
      </span>
    );
  }

  if (category?.isActive === false) {
    return (
      <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
        Kapalı
      </span>
    );
  }

  return (
    <span className="rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
      Aktif
    </span>
  );
}

function CategoryForm({
  title,
  submitLabel,
  form,
  saving,
  error,
  onChange,
  onAutoSlug,
  onSubmit,
  onCancel,
  onDelete,
  deleting
}) {
  const seoDescriptionLength = String(form.seoDescription || '').length;

  return (
    <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-950">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-xl font-black tracking-tight text-slate-950 dark:text-white">{title}</h3>
          <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
            Önce kategori oluştur, sonra yazıları bu kategori altında yönet.
          </p>
        </div>
        {onCancel && (
          <div className="flex items-center gap-2">
            {onDelete ? (
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="rounded-xl border border-red-200 px-3 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
              >
                {deleting ? 'Siliniyor...' : 'Sil'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              İptal
            </button>
          </div>
        )}
      </div>

      {error ? (
        <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <form className="mt-6 grid gap-5 md:grid-cols-2" onSubmit={onSubmit}>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Kategori Adı
          </span>
          <input
            type="text"
            value={form.name}
            onChange={(event) => onChange('name', event.target.value)}
            className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            placeholder="Örn: Mutfak Rehberleri"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Slug
          </span>
          <div className="flex gap-2">
            <input
              type="text"
              value={form.slug}
              onChange={(event) => onChange('slug', event.target.value)}
              className="flex-1 rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              placeholder="mutfak-rehberleri"
            />
            <button
              type="button"
              onClick={onAutoSlug}
              className="rounded-2xl border border-slate-300 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Otomatik
            </button>
          </div>
        </label>

        <label className="md:col-span-2 flex flex-col gap-2">
          <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Açıklama
          </span>
          <textarea
            value={form.description}
            onChange={(event) => onChange('description', event.target.value)}
            rows={4}
            className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            placeholder="Bu kategori hangi içerikleri toplayacak?"
          />
        </label>

        <label className="md:col-span-2 flex flex-col gap-2">
          <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Kategori Giriş Metni
          </span>
          <textarea
            value={form.introContent}
            onChange={(event) => onChange('introContent', event.target.value)}
            rows={5}
            className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            placeholder="Bu kategori, tarif aramaktan öte ev mutfağını stok, bütçe, porsiyon ve planlama ile yönetmek isteyenler için hazırlanmıştır."
          />
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Kategori landing sayfasındaki 80-140 kelimelik niyet açıklaması için kullanılır.
          </div>
        </label>

        <label className="md:col-span-2 flex flex-col gap-2">
          <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            SEO Title
          </span>
          <input
            type="text"
            value={form.seoTitle}
            onChange={(event) => onChange('seoTitle', event.target.value)}
            className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            placeholder="Mutfak Yönetim Sistemi | Ev İçin Akıllı Mutfak Planı"
          />
        </label>

        <label className="md:col-span-2 flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              SEO Description
            </span>
            <span className={`text-xs ${seoDescriptionLength > 160 ? 'text-red-600 dark:text-red-300' : 'text-slate-500 dark:text-slate-400'}`}>
              {seoDescriptionLength}/160
            </span>
          </div>
          <textarea
            value={form.seoDescription}
            onChange={(event) => onChange('seoDescription', event.target.value)}
            rows={3}
            className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            placeholder="Ev mutfağını stok, bütçe, porsiyon, haftalık yemek planı ve günlük kararlarla yöneten mutfak yönetim sistemi içerikleri."
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Öncelik
          </span>
          <input
            type="number"
            value={form.priority}
            onChange={(event) => onChange('priority', Number(event.target.value || 0))}
            className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            min="0"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Sıra
          </span>
          <input
            type="number"
            value={form.sortOrder}
            onChange={(event) => onChange('sortOrder', Number(event.target.value || 0))}
            className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            min="0"
          />
        </label>

        <div className="md:col-span-2 grid gap-4 xl:grid-cols-2">
          <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-slate-800 dark:text-slate-200">
            <input
              type="checkbox"
              checked={form.isPublished}
              onChange={(event) => onChange('isPublished', event.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            <span>Bu kategori landing sayfası public olarak yayınlansın</span>
          </label>

          <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-slate-800 dark:text-slate-200">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => onChange('isActive', event.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            <span>Bu kategori aktif olarak içerik kabul etsin</span>
          </label>
        </div>

        <div className="md:col-span-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200">
          `isActive` yeni yazı ilişkilendirmesini, `isPublished` ise kategori landing sayfasının public görünürlüğünü yönetir.
        </div>

        <div className="md:col-span-2 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="rounded-2xl bg-primary px-5 py-3 text-sm font-black text-white transition hover:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {saving ? 'Kaydediliyor...' : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

function PostEditor({
  title,
  submitLabel,
  form,
  categories,
  saving,
  uploadingCoverImage,
  uploadingOgImage,
  error,
  onChange,
  onUploadCoverImage,
  onUploadOgImage,
  onAutoSlug,
  onSubmit,
  onCancel,
  onDelete,
  deleting
}) {
  const seoDescriptionLength = String(form.seoMetaDescription || '').length;
  const effectiveExcerpt = form.excerpt || stripHtmlPreview(form.htmlContent);
  const resolvedCoverImageUrl = resolveBlogImageUrl(form.coverImageUrl);
  const resolvedOgImageUrl = resolveBlogImageUrl(form.ogImageUrl);

  return (
    <div className="space-y-6">
      <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-950">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-black tracking-tight text-slate-950 dark:text-white">{title}</h3>
            <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
              HTML editör, SEO alanları ve yayın lifecycle aynı akışta ilerler.
            </p>
          </div>
        {onCancel && (
          <div className="flex items-center gap-2">
            {onDelete ? (
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="rounded-xl border border-red-200 px-3 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
              >
                {deleting ? 'Siliniyor...' : 'Sil'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              İptal
            </button>
          </div>
        )}
      </div>

        {error ? (
          <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300">
            {error}
          </div>
        ) : null}

        <form className="mt-6 min-w-0 space-y-6" onSubmit={onSubmit}>
          <div className="grid gap-5 xl:grid-cols-2">
            <div className="md:col-span-2 flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Kapak Görseli Upload
              </span>
              <div className="rounded-2xl border border-dashed border-slate-300 p-3 dark:border-slate-700 dark:bg-slate-900">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={onUploadCoverImage}
                  disabled={uploadingCoverImage}
                  className="block w-full text-sm text-slate-700 file:mr-3 file:rounded-xl file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-sm file:font-black file:text-slate-700 hover:file:bg-slate-200 disabled:opacity-60 dark:text-slate-200 dark:file:bg-slate-800 dark:file:text-slate-100"
                />
                <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  {uploadingCoverImage ? 'Görsel yükleniyor...' : 'Dosya seçtiğinde kapak görseli otomatik yüklenir. Manuel URL alanı aşağıda yine duruyor.'}
                </div>
              </div>
            </div>

            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Başlık
              </span>
              <input
                type="text"
                value={form.title}
                onChange={(event) => onChange('title', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                placeholder="Yazı başlığını gir"
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Slug
              </span>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.slug}
                  onChange={(event) => onChange('slug', event.target.value)}
                  className="flex-1 rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  placeholder="bugun-ne-yemek-yapsam"
                />
                <button
                  type="button"
                  onClick={onAutoSlug}
                  className="rounded-2xl border border-slate-300 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Otomatik
                </button>
              </div>
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                İçerik Türü
              </span>
              <select
                value={form.contentType}
                onChange={(event) => onChange('contentType', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              >
                {CONTENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Kategori
              </span>
              <select
                value={form.categoryId}
                onChange={(event) => onChange('categoryId', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              >
                <option value="">Kategori Seç</option>
                {categories.map((category) => (
                  <option key={category.id} value={String(category.id)}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Durum
              </span>
              <select
                value={form.status}
                onChange={(event) => onChange('status', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              >
                {BLOG_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Sıra
              </span>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(event) => onChange('sortOrder', Number(event.target.value || 0))}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </label>
          </div>

          <div className="grid gap-5 xl:grid-cols-2">
            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Kapak Görseli URL
              </span>
              <input
                type="text"
                value={form.coverImageUrl}
                onChange={(event) => onChange('coverImageUrl', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                placeholder="https://..."
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Kapak Alt Metni
              </span>
              <input
                type="text"
                value={form.coverImageAlt}
                onChange={(event) => onChange('coverImageAlt', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                placeholder="Görselin ne anlattığını yaz"
              />
            </label>

            <label className="md:col-span-2 flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Kapak Açıklaması
              </span>
              <input
                type="text"
                value={form.coverImageCaption}
                onChange={(event) => onChange('coverImageCaption', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                placeholder="İstersen kısa görsel caption"
              />
            </label>
          </div>

          <div className="grid gap-5">
            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Özet
              </span>
              <textarea
                value={form.excerpt}
                onChange={(event) => onChange('excerpt', event.target.value)}
                rows={3}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                placeholder="Boş bırakırsan içerikten otomatik kısa özet üretilir"
              />
            </label>

            <div className="rounded-[1.5rem] border border-slate-200 p-4 dark:border-slate-800">
              <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                HTML Editör
              </div>
              <div className="blog-quill-editor min-w-0 overflow-hidden rounded-2xl bg-white dark:bg-slate-900">
                <ReactQuill
                  theme="snow"
                  value={form.htmlContent}
                  onChange={(value) => onChange('htmlContent', value)}
                  modules={QUILL_MODULES}
                />
              </div>
            </div>
          </div>

          <div className="grid gap-5 xl:grid-cols-2">
            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                SEO Meta Title
              </span>
              <input
                type="text"
                value={form.seoMetaTitle}
                onChange={(event) => onChange('seoMetaTitle', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                placeholder="Arama sonuçlarında görünecek başlık"
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="flex items-center justify-between text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                <span>SEO Meta Description</span>
                <span className={seoDescriptionLength > 160 ? 'text-red-600 dark:text-red-300' : ''}>
                  {seoDescriptionLength}/160
                </span>
              </span>
              <textarea
                value={form.seoMetaDescription}
                onChange={(event) => onChange('seoMetaDescription', event.target.value)}
                rows={4}
                maxLength={160}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                placeholder="SEO açıklaması maksimum 160 karakter olmalı"
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Canonical URL
              </span>
              <input
                type="text"
                value={form.canonicalUrl}
                onChange={(event) => onChange('canonicalUrl', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                placeholder="Boş bırakılırsa otomatik üretilir"
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                OG Görsel URL
              </span>
              <input
                type="text"
                value={form.ogImageUrl}
                onChange={(event) => onChange('ogImageUrl', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                placeholder="Boşsa kapak görseli kullanılır"
              />
            </label>
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_240px]">
            <div className="rounded-[1.5rem] border border-dashed border-slate-300 p-4 dark:border-slate-700 dark:bg-slate-900">
              <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                OG Görsel Upload
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                Paylaşım kartları için kare görsel yükle. Öneri: 1200x1200.
              </p>
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={onUploadOgImage}
                disabled={uploadingOgImage}
                className="mt-4 block w-full text-sm text-slate-700 file:mr-3 file:rounded-xl file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-sm file:font-black file:text-slate-700 hover:file:bg-slate-200 disabled:opacity-60 dark:text-slate-200 dark:file:bg-slate-800 dark:file:text-slate-100"
              />
              <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {uploadingOgImage ? 'OG görseli yükleniyor...' : 'Yüklenen dosya sadece OG alanına yazılır. Boşsa kapak görseli kullanılır.'}
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                OG Önizleme
              </div>
              <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-900">
                {resolvedOgImageUrl ? (
                  <img
                    src={resolvedOgImageUrl}
                    alt="OG görsel önizlemesi"
                    className="aspect-square w-full object-cover"
                  />
                ) : (
                  <div className="flex aspect-square items-center justify-center px-4 text-center text-xs leading-6 text-slate-400">
                    Kare OG görsel burada görünür
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid gap-3 xl:grid-cols-3">
            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-slate-800 dark:text-slate-200">
              <input
                type="checkbox"
                checked={form.isFeatured}
                onChange={(event) => onChange('isFeatured', event.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              <span>Öne çıkan yazı</span>
            </label>

            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-slate-800 dark:text-slate-200">
              <input
                type="checkbox"
                checked={form.isIndexed}
                onChange={(event) => onChange('isIndexed', event.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              <span>Arama motoruna açık (blogda görünmeyi etkilemez)</span>
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                JSON-LD Türü
              </span>
              <select
                value={form.jsonLdType}
                onChange={(event) => onChange('jsonLdType', event.target.value)}
                className="rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              >
                <option value="Article">Article</option>
                <option value="HowTo">HowTo</option>
                <option value="FAQPage">FAQPage</option>
              </select>
            </label>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="rounded-2xl bg-primary px-5 py-3 text-sm font-black text-white transition hover:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saving ? 'Kaydediliyor...' : submitLabel}
            </button>
          </div>
        </form>
      </div>

      <div className="grid min-w-0 gap-6 2xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <div className="min-w-0 rounded-[1.75rem] border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-950">
          <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Canlı Önizleme
          </div>
          <h4 className="font-serif text-3xl font-black tracking-tight text-slate-950 dark:text-white">
            {form.title || 'Başlık burada görünecek'}
          </h4>
          <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
            {effectiveExcerpt || 'Özet burada görünecek.'}
          </p>
          {resolvedCoverImageUrl ? (
            <img
              src={resolvedCoverImageUrl}
              alt={form.coverImageAlt || 'Kapak görseli'}
              className="mt-5 aspect-[16/9] w-full rounded-xl object-cover"
            />
          ) : null}
          <div
            className="prose prose-slate mt-6 max-w-none prose-headings:font-serif prose-headings:font-bold prose-a:text-primary dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: form.htmlContent || '<p>İçerik önizlemesi burada görünecek.</p>' }}
          />
        </div>

        <div className="min-w-0 space-y-6">
          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-950">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              İçerik Özeti
            </div>
            <div className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
              {effectiveExcerpt || 'Henüz içerik eklenmedi.'}
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-950">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Publish Kontrolü
            </div>
            <ul className="mt-4 space-y-2 text-sm text-slate-700 dark:text-slate-200">
              <li>{form.title ? '✓' : '•'} Başlık</li>
              <li>{form.coverImageAlt ? '✓' : '•'} Kapak alt metni</li>
              <li>{form.seoMetaTitle ? '✓' : '•'} SEO meta title</li>
              <li>{seoDescriptionLength > 0 && seoDescriptionLength <= 160 ? '✓' : '•'} SEO meta description</li>
              <li>{stripHtmlPreview(form.htmlContent) ? '✓' : '•'} İçerik gövdesi</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminBlogPage() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY_FORM);
  const [categoryFormError, setCategoryFormError] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [savingCategory, setSavingCategory] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [postForm, setPostForm] = useState(EMPTY_POST_FORM);
  const [postFormError, setPostFormError] = useState('');
  const [editingPostId, setEditingPostId] = useState(null);
  const [savingPost, setSavingPost] = useState(false);
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false);
  const [uploadingOgImage, setUploadingOgImage] = useState(false);
  const [reorderPosts, setReorderPosts] = useState([]);
  const [reorderDirty, setReorderDirty] = useState(false);
  const [savingReorder, setSavingReorder] = useState(false);
  const [draggedPostId, setDraggedPostId] = useState(null);
  const [selectedPostIds, setSelectedPostIds] = useState([]);

  const fetchAdminData = async () => {
    const [catRes, postRes] = await Promise.all([
      fetch(`${API_BASE_URL}/api/admin/blog/categories`, { headers: getAdminHeaders() }),
      fetch(`${API_BASE_URL}/api/admin/blog/posts`, { headers: getAdminHeaders() })
    ]);

    const [catJson, postJson] = await Promise.all([
      catRes.json().catch(() => ({})),
      postRes.json().catch(() => ({}))
    ]);

    if (!catRes.ok) throw new Error(catJson?.error || 'blog_categories_fetch_failed');
    if (!postRes.ok) throw new Error(postJson?.error || 'blog_posts_fetch_failed');

    setCategories(Array.isArray(catJson?.categories) ? catJson.categories : []);
    setPosts(Array.isArray(postJson?.posts) ? postJson.posts : []);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        await fetchAdminData();
      } catch (_error) {
        if (!mounted) return;
        setError('Blog admin verileri şu anda yüklenemedi.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const stats = useMemo(() => {
    const publishedCount = posts.filter((post) => post.status === 'published').length;
    const draftCount = posts.filter((post) => post.status === 'draft').length;
    const archivedCount = posts.filter((post) => post.status === 'archived').length;
    return { publishedCount, draftCount, archivedCount };
  }, [posts]);

  const activeCategories = useMemo(
    () => categories.filter((category) => category.isDeleted !== true),
    [categories]
  );

  const postAssignableCategories = useMemo(
    () => activeCategories.filter((category) => category.isActive !== false),
    [activeCategories]
  );

  const visiblePosts = useMemo(
    () => posts.filter((post) => post.status !== 'archived'),
    [posts]
  );

  const archivedPosts = useMemo(
    () => posts.filter((post) => post.status === 'archived'),
    [posts]
  );

  const featuredPosts = useMemo(
    () => reorderPosts.filter((post) => Boolean(post.isFeatured)),
    [reorderPosts]
  );

  const managedPosts = activeMenu === 'archive'
    ? archivedPosts
    : activeMenu === 'featured'
      ? featuredPosts
      : visiblePosts;

  useEffect(() => {
    setSelectedPostIds((current) => current.filter((id) => managedPosts.some((post) => post.id === id)));
  }, [managedPosts]);

  useEffect(() => {
    const ordered = [...visiblePosts].sort((a, b) => {
      if (Number(Boolean(b.isFeatured)) !== Number(Boolean(a.isFeatured))) {
        return Number(Boolean(b.isFeatured)) - Number(Boolean(a.isFeatured));
      }
      if (Number(a.sortOrder || 0) !== Number(b.sortOrder || 0)) {
        return Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
      }
      return Number(a.id || 0) - Number(b.id || 0);
    });
    setReorderPosts(ordered);
    setReorderDirty(false);
  }, [visiblePosts]);

  const handleCategoryFieldChange = (field, value) => {
    setCategoryForm((current) => ({ ...current, [field]: value }));
  };

  const resetCategoryEditor = () => {
    setEditingCategoryId(null);
    setCategoryForm(EMPTY_CATEGORY_FORM);
    setCategoryFormError('');
  };

  const handleAutoCategorySlug = () => {
    setCategoryForm((current) => ({
      ...current,
      slug: slugifyClient(current.name)
    }));
  };

  const handleStartCreateCategory = () => {
    resetCategoryEditor();
    setActiveMenu('new-category');
  };

  const handleStartEditCategory = (category) => {
    setEditingCategoryId(category.id);
    setCategoryForm(mapCategoryToForm(category));
    setCategoryFormError('');
    setActiveMenu('new-category');
  };

  const handleSubmitCategory = async (event) => {
    event.preventDefault();
    try {
      setSavingCategory(true);
      setCategoryFormError('');

      const payload = {
        ...categoryForm,
        slug: categoryForm.slug || slugifyClient(categoryForm.name)
      };

      const isEditing = editingCategoryId !== null;
      const endpoint = isEditing
        ? `${API_BASE_URL}/api/admin/blog/categories/${editingCategoryId}`
        : `${API_BASE_URL}/api/admin/blog/categories`;
      const method = isEditing ? 'PATCH' : 'POST';

      const res = await fetch(endpoint, {
        method,
        headers: getAdminHeaders(),
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(
          json?.error,
          isEditing ? 'Kategori güncellenemedi.' : 'Kategori oluşturulamadı.'
        ));
      }

      await fetchAdminData();
      resetCategoryEditor();
      setActiveMenu('categories');
    } catch (submitError) {
      setCategoryFormError(submitError?.message || 'Kategori kaydedilemedi.');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    const confirmed = window.confirm(
      `"${category.name}" kategorisini silmek istiyor musun? Kategori public alandan kalkar, pasife alınır ve bu kategoriye bağlı yazılar arşivlenir.`
    );
    if (!confirmed) return;

    try {
      setActionLoadingId(category.id);
      const res = await fetch(`${API_BASE_URL}/api/admin/blog/categories/${category.id}`, {
        method: 'DELETE',
        headers: getAdminHeaders()
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(json?.error, 'Kategori pasifleştirilemedi.'));
      }

      await fetchAdminData();
      if (editingCategoryId === category.id) {
        resetCategoryEditor();
      }
    } catch (deleteError) {
      setError(deleteError?.message || 'Kategori pasifleştirilemedi.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteCategoryFromEditor = async () => {
    if (!editingCategoryId) return;
    const targetCategory = categories.find((category) => category.id === editingCategoryId);
    if (!targetCategory) return;
    await handleDeleteCategory(targetCategory);
    resetCategoryEditor();
    setActiveMenu('categories');
  };

  const handlePostFieldChange = (field, value) => {
    setPostForm((current) => ({ ...current, [field]: value }));
  };

  const resetPostEditor = () => {
    setEditingPostId(null);
    setPostForm(EMPTY_POST_FORM);
    setPostFormError('');
  };

  const handleAutoPostSlug = () => {
    setPostForm((current) => ({
      ...current,
      slug: slugifyClient(current.title)
    }));
  };

  const handleUploadCoverImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingCoverImage(true);
      setPostFormError('');

      const formData = new FormData();
      formData.append('image', file);

      const res = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        body: formData
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok) {
        throw new Error(json?.message || 'Görsel yüklenemedi.');
      }

      setPostForm((current) => ({
        ...current,
        coverImageUrl: json?.imageUrl || current.coverImageUrl,
        ogImageUrl: current.ogImageUrl || json?.imageUrl || current.ogImageUrl
      }));
    } catch (uploadError) {
      setPostFormError(uploadError?.message || 'Görsel yüklenemedi.');
    } finally {
      setUploadingCoverImage(false);
      event.target.value = '';
    }
  };

  const handleUploadOgImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingOgImage(true);
      setPostFormError('');

      const formData = new FormData();
      formData.append('image', file);

      const res = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        body: formData
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok) {
        throw new Error(json?.message || 'OG görseli yüklenemedi.');
      }

      setPostForm((current) => ({
        ...current,
        ogImageUrl: json?.imageUrl || current.ogImageUrl
      }));
    } catch (uploadError) {
      setPostFormError(uploadError?.message || 'OG görseli yüklenemedi.');
    } finally {
      setUploadingOgImage(false);
      event.target.value = '';
    }
  };

  const handleStartCreatePost = () => {
    resetPostEditor();
    setActiveMenu('new-post');
  };

  const handleStartEditPost = async (postId) => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE_URL}/api/admin/blog/posts/${postId}`, {
        headers: getAdminHeaders()
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(json?.error, 'Yazı bilgisi alınamadı.'));
      }

      setEditingPostId(postId);
      setPostForm(mapPostToForm(json.post));
      setPostFormError('');
      setActiveMenu('new-post');
    } catch (fetchError) {
      setError(fetchError?.message || 'Yazı bilgisi alınamadı.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPost = async (event) => {
    event.preventDefault();
    try {
      setSavingPost(true);
      setPostFormError('');

      const payload = {
        ...postForm,
        slug: postForm.slug || slugifyClient(postForm.title),
        categoryId: postForm.categoryId ? Number(postForm.categoryId) : null
      };

      const isEditing = editingPostId !== null;
      const endpoint = isEditing
        ? `${API_BASE_URL}/api/admin/blog/posts/${editingPostId}`
        : `${API_BASE_URL}/api/admin/blog/posts`;
      const method = isEditing ? 'PATCH' : 'POST';

      const res = await fetch(endpoint, {
        method,
        headers: getAdminHeaders(),
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(
          json?.error,
          isEditing ? 'Yazı güncellenemedi.' : 'Yazı oluşturulamadı.'
        ));
      }

      await fetchAdminData();
      resetPostEditor();
      setActiveMenu('posts');
    } catch (submitError) {
      setPostFormError(submitError?.message || 'Yazı kaydedilemedi.');
    } finally {
      setSavingPost(false);
    }
  };

  const handleArchivePost = async (post) => {
    const confirmed = window.confirm(
      `"${post.title}" yazısını silmek istiyor musun? Yazı public alandan kalkar ve arşive taşınır.`
    );
    if (!confirmed) return;

    try {
      setActionLoadingId(`post-${post.id}`);
      const res = await fetch(`${API_BASE_URL}/api/admin/blog/posts/${post.id}`, {
        method: 'DELETE',
        headers: getAdminHeaders()
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(json?.error, 'Yazı arşivlenemedi.'));
      }

      await fetchAdminData();
      if (editingPostId === post.id) {
        resetPostEditor();
      }
    } catch (archiveError) {
      setError(archiveError?.message || 'Yazı arşivlenemedi.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleTogglePostSelection = (postId) => {
    setSelectedPostIds((current) =>
      current.includes(postId)
        ? current.filter((id) => id !== postId)
        : [...current, postId]
    );
  };

  const handleSelectAllPosts = () => {
    setSelectedPostIds(managedPosts.map((post) => post.id));
  };

  const handleClearPostSelection = () => {
    setSelectedPostIds([]);
  };

  const handleBulkArchivePosts = async () => {
    if (selectedPostIds.length === 0) return;

    const selectedPosts = visiblePosts.filter((post) => selectedPostIds.includes(post.id));
    const confirmed = window.confirm(
      `${selectedPosts.length} yazıyı toplu silmek istiyor musun? Yazılar public alandan kalkar ve arşive taşınır.`
    );
    if (!confirmed) return;

    try {
      setActionLoadingId('bulk-post-delete');

      for (const post of selectedPosts) {
        const res = await fetch(`${API_BASE_URL}/api/admin/blog/posts/${post.id}`, {
          method: 'DELETE',
          headers: getAdminHeaders()
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(formatApiError(json?.error, `"${post.title}" arşivlenemedi.`));
        }
      }

      await fetchAdminData();
      setSelectedPostIds([]);
      if (editingPostId && selectedPostIds.includes(editingPostId)) {
        resetPostEditor();
        setActiveMenu('posts');
      }
    } catch (bulkArchiveError) {
      setError(bulkArchiveError?.message || 'Toplu silme tamamlanamadı.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRestorePost = async (post) => {
    const confirmed = window.confirm(
      `"${post.title}" yazısını arşivden çıkarıp taslağa almak istiyor musun?`
    );
    if (!confirmed) return;

    try {
      setActionLoadingId(`restore-${post.id}`);
      const res = await fetch(`${API_BASE_URL}/api/admin/blog/posts/${post.id}/restore`, {
        method: 'POST',
        headers: getAdminHeaders()
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(json?.error, 'Yazı arşivden çıkarılamadı.'));
      }

      await fetchAdminData();
      setSelectedPostIds((current) => current.filter((id) => id !== post.id));
      if (editingPostId === post.id) {
        resetPostEditor();
      }
    } catch (restoreError) {
      setError(restoreError?.message || 'Yazı arşivden çıkarılamadı.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handlePermanentDeletePost = async (post) => {
    const confirmed = window.confirm(
      `"${post.title}" yazısını kalıcı olarak silmek istiyor musun? Bu işlem geri alınamaz.`
    );
    if (!confirmed) return;

    try {
      setActionLoadingId(`permanent-${post.id}`);
      const res = await fetch(`${API_BASE_URL}/api/admin/blog/posts/${post.id}/permanent`, {
        method: 'DELETE',
        headers: getAdminHeaders()
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(json?.error, 'Yazı kalıcı olarak silinemedi.'));
      }

      await fetchAdminData();
      setSelectedPostIds((current) => current.filter((id) => id !== post.id));
      if (editingPostId === post.id) {
        resetPostEditor();
      }
    } catch (deleteError) {
      setError(deleteError?.message || 'Yazı kalıcı olarak silinemedi.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleBulkPermanentDeletePosts = async () => {
    if (selectedPostIds.length === 0) return;

    const selectedPosts = archivedPosts.filter((post) => selectedPostIds.includes(post.id));
    const confirmed = window.confirm(
      `${selectedPosts.length} arşiv yazısını kalıcı olarak silmek istiyor musun? Bu işlem geri alınamaz.`
    );
    if (!confirmed) return;

    try {
      setActionLoadingId('bulk-post-permanent-delete');

      for (const post of selectedPosts) {
        const res = await fetch(`${API_BASE_URL}/api/admin/blog/posts/${post.id}/permanent`, {
          method: 'DELETE',
          headers: getAdminHeaders()
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(formatApiError(json?.error, `"${post.title}" kalıcı olarak silinemedi.`));
        }
      }

      await fetchAdminData();
      setSelectedPostIds([]);
      if (editingPostId && selectedPostIds.includes(editingPostId)) {
        resetPostEditor();
        setActiveMenu('archive');
      }
    } catch (bulkDeleteError) {
      setError(bulkDeleteError?.message || 'Toplu kalıcı silme tamamlanamadı.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteFromEditor = async () => {
    if (!editingPostId) return;
    const targetPost = posts.find((post) => post.id === editingPostId);
    if (!targetPost) return;
    await handleArchivePost(targetPost);
    resetPostEditor();
    setActiveMenu('posts');
  };

  const handlePublishPost = async (post) => {
    try {
      setActionLoadingId(`publish-${post.id}`);
      const res = await fetch(`${API_BASE_URL}/api/admin/blog/posts/${post.id}/publish`, {
        method: 'POST',
        headers: getAdminHeaders()
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(json?.error, 'Yazı yayınlanamadı.'));
      }
      await fetchAdminData();
    } catch (publishError) {
      setError(publishError?.message || 'Yazı yayınlanamadı.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDragStart = (postId) => {
    setDraggedPostId(postId);
  };

  const handleDropOnPost = (targetPostId) => {
    if (!draggedPostId || draggedPostId === targetPostId) return;
    setReorderPosts((current) => {
      const draggedIndex = current.findIndex((post) => post.id === draggedPostId);
      const targetIndex = current.findIndex((post) => post.id === targetPostId);
      if (draggedIndex === -1 || targetIndex === -1) return current;
      const next = [...current];
      const [draggedItem] = next.splice(draggedIndex, 1);
      next.splice(targetIndex, 0, draggedItem);
      return next;
    });
    setReorderDirty(true);
    setDraggedPostId(null);
  };

  const handleMovePost = (postId, direction) => {
    setReorderPosts((current) => {
      const index = current.findIndex((post) => post.id === postId);
      if (index === -1) return current;
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= current.length) return current;
      const next = [...current];
      const [item] = next.splice(index, 1);
      next.splice(targetIndex, 0, item);
      return next;
    });
    setReorderDirty(true);
  };

  const handleToggleFeaturedInBoard = (postId) => {
    setReorderPosts((current) =>
      current.map((post) =>
        post.id === postId
          ? { ...post, isFeatured: !post.isFeatured }
          : post
      )
    );
    setReorderDirty(true);
  };

  const handleSaveReorder = async () => {
    try {
      setSavingReorder(true);
      setError('');
      const items = reorderPosts.map((post, index) => ({
        id: post.id,
        sortOrder: index,
        isFeatured: Boolean(post.isFeatured)
      }));

      const res = await fetch(`${API_BASE_URL}/api/admin/blog/posts/reorder`, {
        method: 'POST',
        headers: getAdminHeaders(),
        body: JSON.stringify({ items })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(json?.error, 'Sıralama kaydedilemedi.'));
      }

      await fetchAdminData();
      setReorderDirty(false);
    } catch (saveError) {
      setError(saveError?.message || 'Sıralama kaydedilemedi.');
    } finally {
      setSavingReorder(false);
    }
  };

  const renderReorderBoard = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black tracking-tight text-slate-950 dark:text-white">Yazı Sıralama Board'u</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Yazıları sürükleyip bırakarak sırala, gerekirse featured durumunu da buradan ayarla.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {reorderDirty ? (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
              Kaydedilmemiş değişiklik
            </span>
          ) : (
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
              Senkron
            </span>
          )}
          <button
            type="button"
            onClick={handleSaveReorder}
            disabled={!reorderDirty || savingReorder}
            className="rounded-2xl bg-primary px-4 py-3 text-sm font-black text-white transition hover:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {savingReorder ? 'Kaydediliyor...' : 'Sıralamayı Kaydet'}
          </button>
        </div>
      </div>

      {reorderPosts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-sm leading-7 text-slate-600 dark:border-slate-700 dark:text-slate-300">
          Sıralanacak yazı bulunamadı.
        </div>
      ) : (
        <div className="space-y-3">
          {reorderPosts.map((post, index) => (
            <div
              key={post.id}
              draggable
              onDragStart={() => handleDragStart(post.id)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => handleDropOnPost(post.id)}
              onDragEnd={() => setDraggedPostId(null)}
              className={`rounded-[1.5rem] border bg-white p-4 transition dark:bg-slate-950 ${
                draggedPostId === post.id
                  ? 'border-primary shadow-lg shadow-primary/10 dark:border-primary'
                  : 'border-slate-200 dark:border-slate-800'
              }`}
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-black text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    #{index + 1}
                  </div>
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="material-symbols-outlined cursor-grab text-slate-400">drag_indicator</span>
                      <h3 className="font-serif text-xl font-bold tracking-tight text-slate-950 dark:text-white">
                        {post.title}
                      </h3>
                      <span className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${getStatusTone(post.status)}`}>
                        {post.status}
                      </span>
                      {post.isFeatured ? (
                        <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-primary">
                          Featured
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      /blog/{post.slug} • {post.category?.name || 'Kategorisiz'} • sortOrder {index}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleMovePost(post.id, 'up')}
                    className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Yukarı
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMovePost(post.id, 'down')}
                    className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Aşağı
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleFeaturedInBoard(post.id)}
                    className={`rounded-xl px-3 py-2 text-xs font-black uppercase tracking-[0.18em] transition ${
                      post.isFeatured
                        ? 'bg-primary text-white hover:opacity-90'
                        : 'border border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800'
                    }`}
                  >
                    {post.isFeatured ? 'Featured Kaldır' : 'Featured Yap'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCategoryList = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black tracking-tight text-slate-950 dark:text-white">Kategori Listesi</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Yazılardan önce kategori omurgasını burada kuruyorsun.
          </p>
        </div>
        <button
          type="button"
          onClick={handleStartCreateCategory}
          className="rounded-2xl bg-primary px-4 py-3 text-sm font-black text-white transition hover:scale-[0.99]"
        >
          Yeni Kategori
        </button>
      </div>

      {activeCategories.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-sm leading-7 text-slate-600 dark:border-slate-700 dark:text-slate-300">
          Henüz kategori yok. Önce ilk kategori yapısını oluştur.
        </div>
      ) : (
        activeCategories.map((category) => (
          <div
            key={category.id}
            className="rounded-[1.5rem] border border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950"
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-lg font-black tracking-tight text-slate-950 dark:text-white">
                    {category.name}
                  </div>
                  <CategoryStatusBadge category={category} />
                </div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  /blog/{category.slug} • sıra #{category.sortOrder} • öncelik {category.priority || 0}
                </div>
                <p className="max-w-2xl text-sm leading-7 text-slate-600 dark:text-slate-300">
                  {category.description || 'Açıklama girilmedi.'}
                </p>
                <div className="flex flex-wrap gap-2 text-[11px] font-black uppercase tracking-[0.18em]">
                  <span className={`rounded-full px-3 py-1 ${category.isPublished ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                    {category.isPublished ? 'Public Açık' : 'Public Kapalı'}
                  </span>
                  {category.seoTitle ? (
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                      SEO Title Var
                    </span>
                  ) : null}
                  {category.seoDescription ? (
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                      SEO Description Var
                    </span>
                  ) : null}
                  {category.introContent ? (
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                      Landing Intro Var
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleStartEditCategory(category)}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Düzenle
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteCategory(category)}
                  disabled={actionLoadingId === category.id}
                  className="rounded-xl border border-red-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
                >
                  {actionLoadingId === category.id ? 'Siliniyor...' : 'Sil'}
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderPostList = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black tracking-tight text-slate-950 dark:text-white">Yazı Listesi</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Taslak, yayında ve arşiv yazıları buradan yönet.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {visiblePosts.length > 0 ? (
            <>
              <button
                type="button"
                onClick={handleSelectAllPosts}
                className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Tümünü Seç
              </button>
              {selectedPostIds.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={handleClearPostSelection}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Seçimi Temizle
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkArchivePosts}
                    disabled={actionLoadingId === 'bulk-post-delete'}
                    className="rounded-xl border border-red-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
                  >
                    {actionLoadingId === 'bulk-post-delete'
                      ? 'Siliniyor...'
                      : `Toplu Sil (${selectedPostIds.length})`}
                  </button>
                </>
              ) : null}
            </>
          ) : null}
          <button
            type="button"
            onClick={handleStartCreatePost}
            className="rounded-2xl bg-primary px-4 py-3 text-sm font-black text-white transition hover:scale-[0.99]"
          >
            Yeni Yazı
          </button>
        </div>
      </div>

      {visiblePosts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-sm leading-7 text-slate-600 dark:border-slate-700 dark:text-slate-300">
          Aktif yazı yok. Yeni bir yazı oluşturabilir veya arşiv dışındaki yazıları burada yönetebilirsin.
        </div>
      ) : (
        visiblePosts.map((post) => (
          <div
            key={post.id}
            className="rounded-[1.5rem] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950"
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedPostIds.includes(post.id)}
                      onChange={() => handleTogglePostSelection(post.id)}
                      className="h-4 w-4 rounded border-slate-300 accent-primary"
                    />
                  </label>
                  <h3 className="font-serif text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
                    {post.title}
                  </h3>
                  <span className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${getStatusTone(post.status)}`}>
                    {post.status}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${
                      isPostSeoReady(post)
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300'
                    }`}
                  >
                    {isPostSeoReady(post) ? 'SEO hazır' : 'SEO eksik'}
                  </span>
                  {post.isFeatured ? (
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-primary">
                      Featured
                    </span>
                  ) : null}
                </div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  /blog/{post.slug} • {post.category?.name || 'Kategorisiz'} • {post.contentType}
                </div>
                <p className="max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
                  {post.excerpt || stripHtmlPreview(post.htmlContent) || 'Özet girilmedi.'}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleStartEditPost(post.id)}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Düzenle
                </button>
                {post.status !== 'published' ? (
                  <button
                    type="button"
                    onClick={() => handlePublishPost(post)}
                    disabled={actionLoadingId === `publish-${post.id}`}
                    className="rounded-xl border border-emerald-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-950/20"
                  >
                    {actionLoadingId === `publish-${post.id}` ? 'Yayınlanıyor...' : 'Yayınla'}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleArchivePost(post)}
                  disabled={actionLoadingId === `post-${post.id}`}
                  className="rounded-xl border border-red-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
                >
                  {actionLoadingId === `post-${post.id}` ? 'Siliniyor...' : 'Sil'}
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderWorkingPostList = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black tracking-tight text-slate-950 dark:text-white">Yazı Listesi</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Taslak ve yayındaki içerikleri buradan yönet. Arşiv ayrı bir akışta toplansın.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {visiblePosts.length > 0 ? (
            <>
              <button
                type="button"
                onClick={handleSelectAllPosts}
                className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Tümünü Seç
              </button>
              {selectedPostIds.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={handleClearPostSelection}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Seçimi Temizle
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkArchivePosts}
                    disabled={actionLoadingId === 'bulk-post-delete'}
                    className="rounded-xl border border-red-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
                  >
                    {actionLoadingId === 'bulk-post-delete'
                      ? 'Taşınıyor...'
                      : `Toplu Arşive Taşı (${selectedPostIds.length})`}
                  </button>
                </>
              ) : null}
            </>
          ) : null}
          <button
            type="button"
            onClick={handleStartCreatePost}
            className="rounded-2xl bg-primary px-4 py-3 text-sm font-black text-white transition hover:scale-[0.99]"
          >
            Yeni Yazı
          </button>
        </div>
      </div>

      {visiblePosts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-sm leading-7 text-slate-600 dark:border-slate-700 dark:text-slate-300">
          Aktif yazı yok. Yeni bir yazı oluşturabilir veya arşiv dışındaki yazıları burada yönetebilirsin.
        </div>
      ) : (
        visiblePosts.map((post) => (
          <div
            key={post.id}
            className="rounded-[1.5rem] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950"
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedPostIds.includes(post.id)}
                      onChange={() => handleTogglePostSelection(post.id)}
                      className="h-4 w-4 rounded border-slate-300 accent-primary"
                    />
                  </label>
                  <h3 className="font-serif text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
                    {post.title}
                  </h3>
                  <span className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${getStatusTone(post.status)}`}>
                    {post.status}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${
                      isPostSeoReady(post)
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300'
                    }`}
                  >
                    {isPostSeoReady(post) ? 'SEO hazır' : 'SEO eksik'}
                  </span>
                  {post.isFeatured ? (
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-primary">
                      Featured
                    </span>
                  ) : null}
                </div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  /blog/{post.slug} • {post.category?.name || 'Kategorisiz'} • {post.contentType}
                </div>
                <p className="max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
                  {post.excerpt || stripHtmlPreview(post.htmlContent) || 'Özet girilmedi.'}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleStartEditPost(post.id)}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Düzenle
                </button>
                {post.status !== 'published' ? (
                  <button
                    type="button"
                    onClick={() => handlePublishPost(post)}
                    disabled={actionLoadingId === `publish-${post.id}`}
                    className="rounded-xl border border-emerald-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-950/20"
                  >
                    {actionLoadingId === `publish-${post.id}` ? 'Yayınlanıyor...' : 'Yayınla'}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleArchivePost(post)}
                  disabled={actionLoadingId === `post-${post.id}`}
                  className="rounded-xl border border-red-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
                >
                  {actionLoadingId === `post-${post.id}` ? 'Arşive Taşınıyor...' : 'Arşive Taşı'}
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderWorkingPostListV2 = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black tracking-tight text-slate-950 dark:text-white">Yazılar</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Yazıları burada sırala, featured yap ve toplu arşive taşı. Ayrı sıralama ekranına gerek yok.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {reorderDirty ? (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
              Kaydedilmemiş değişiklik
            </span>
          ) : (
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
              Senkron
            </span>
          )}
          <button
            type="button"
            onClick={handleSelectAllPosts}
            disabled={reorderPosts.length === 0}
            className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Tümünü Seç
          </button>
          {selectedPostIds.length > 0 ? (
            <>
              <button
                type="button"
                onClick={handleClearPostSelection}
                className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Seçimi Temizle
              </button>
              <button
                type="button"
                onClick={handleBulkArchivePosts}
                disabled={actionLoadingId === 'bulk-post-delete'}
                className="rounded-xl border border-red-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
              >
                {actionLoadingId === 'bulk-post-delete' ? 'Taşınıyor...' : `Toplu Arşive Taşı (${selectedPostIds.length})`}
              </button>
            </>
          ) : null}
          <button
            type="button"
            onClick={handleSaveReorder}
            disabled={!reorderDirty || savingReorder}
            className="rounded-2xl border border-slate-300 px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {savingReorder ? 'Kaydediliyor...' : 'Düzeni Kaydet'}
          </button>
          <button
            type="button"
            onClick={handleStartCreatePost}
            className="rounded-2xl bg-primary px-4 py-3 text-sm font-black text-white transition hover:scale-[0.99]"
          >
            Yeni Yazı
          </button>
        </div>
      </div>

      {reorderPosts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-sm leading-7 text-slate-600 dark:border-slate-700 dark:text-slate-300">
          Aktif yazı yok. Yeni bir yazı oluşturabilir veya arşiv dışındaki yazıları burada yönetebilirsin.
        </div>
      ) : (
        <div className="space-y-3">
          {reorderPosts.map((post, index) => (
            <div
              key={post.id}
              draggable
              onDragStart={() => handleDragStart(post.id)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => handleDropOnPost(post.id)}
              onDragEnd={() => setDraggedPostId(null)}
              className={`rounded-[1.5rem] border bg-white p-5 transition dark:bg-slate-950 ${
                draggedPostId === post.id
                  ? 'border-primary shadow-lg shadow-primary/10 dark:border-primary'
                  : 'border-slate-200 dark:border-slate-800'
              }`}
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="material-symbols-outlined cursor-grab text-slate-400">drag_indicator</span>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedPostIds.includes(post.id)}
                        onChange={() => handleTogglePostSelection(post.id)}
                        className="h-4 w-4 rounded border-slate-300 accent-primary"
                      />
                    </label>
                    <h3 className="font-serif text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
                      {post.title}
                    </h3>
                    <span className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${getStatusTone(post.status)}`}>
                      {post.status}
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${
                        isPostSeoReady(post)
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300'
                      }`}
                    >
                      {isPostSeoReady(post) ? 'SEO hazır' : 'SEO eksik'}
                    </span>
                    {post.isFeatured ? (
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-primary">
                        Featured
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    /blog/{post.slug} • {post.category?.name || 'Kategorisiz'} • {post.contentType} • sıra {index + 1}
                  </div>
                  <p className="max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
                    {post.excerpt || stripHtmlPreview(post.htmlContent) || 'Özet girilmedi.'}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleStartEditPost(post.id)}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Düzenle
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMovePost(post.id, 'up')}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Yukarı
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMovePost(post.id, 'down')}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Aşağı
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleFeaturedInBoard(post.id)}
                    className={`rounded-xl px-4 py-2 text-xs font-black uppercase tracking-[0.18em] transition ${
                      post.isFeatured
                        ? 'bg-primary text-white hover:opacity-90'
                        : 'border border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800'
                    }`}
                  >
                    {post.isFeatured ? 'Featured Kaldır' : 'Featured Yap'}
                  </button>
                  {post.status !== 'published' ? (
                    <button
                      type="button"
                      onClick={() => handlePublishPost(post)}
                      disabled={actionLoadingId === `publish-${post.id}`}
                      className="rounded-xl border border-emerald-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-950/20"
                    >
                      {actionLoadingId === `publish-${post.id}` ? 'Yayınlanıyor...' : 'Yayınla'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => handleArchivePost(post)}
                    disabled={actionLoadingId === `post-${post.id}`}
                    className="rounded-xl border border-red-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
                  >
                    {actionLoadingId === `post-${post.id}` ? 'Arşive Taşınıyor...' : 'Arşive Taşı'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderArchiveList = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black tracking-tight text-slate-950 dark:text-white">Arşiv</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Arşiv etiketli tüm yazılar burada toplansın; istersen taslağa geri al, istersen kalıcı sil.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {archivedPosts.length > 0 ? (
            <>
              <button
                type="button"
                onClick={handleSelectAllPosts}
                className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Tümünü Seç
              </button>
              {selectedPostIds.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={handleClearPostSelection}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Seçimi Temizle
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkPermanentDeletePosts}
                    disabled={actionLoadingId === 'bulk-post-permanent-delete'}
                    className="rounded-xl border border-red-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
                  >
                    {actionLoadingId === 'bulk-post-permanent-delete'
                      ? 'Siliniyor...'
                      : `Toplu Kalıcı Sil (${selectedPostIds.length})`}
                  </button>
                </>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {archivedPosts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-sm leading-7 text-slate-600 dark:border-slate-700 dark:text-slate-300">
          Arşivde yazı yok. Aktif listeden arşive taşıdığın içerikler burada birikir.
        </div>
      ) : (
        archivedPosts.map((post) => (
          <div
            key={post.id}
            className="rounded-[1.5rem] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950"
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedPostIds.includes(post.id)}
                      onChange={() => handleTogglePostSelection(post.id)}
                      className="h-4 w-4 rounded border-slate-300 accent-primary"
                    />
                  </label>
                  <h3 className="font-serif text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
                    {post.title}
                  </h3>
                  <span className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${getStatusTone(post.status)}`}>
                    {post.status}
                  </span>
                </div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  /blog/{post.slug} • {post.category?.name || 'Kategorisiz'} • {post.contentType}
                </div>
                <p className="max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
                  {post.excerpt || stripHtmlPreview(post.htmlContent) || 'Özet girilmedi.'}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleStartEditPost(post.id)}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Düzenle
                </button>
                <button
                  type="button"
                  onClick={() => handleRestorePost(post)}
                  disabled={actionLoadingId === `restore-${post.id}`}
                  className="rounded-xl border border-emerald-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-950/20"
                >
                  {actionLoadingId === `restore-${post.id}` ? 'Geri Alınıyor...' : 'Taslağa Geri Al'}
                </button>
                <button
                  type="button"
                  onClick={() => handlePermanentDeletePost(post)}
                  disabled={actionLoadingId === `permanent-${post.id}`}
                  className="rounded-xl border border-red-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
                >
                  {actionLoadingId === `permanent-${post.id}` ? 'Siliniyor...' : 'Kalıcı Sil'}
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderFeaturedBoard = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black tracking-tight text-slate-950 dark:text-white">Öne Çıkanlar</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Hero alanında tek yazı yerine carousel dönecek. Burada featured yazıları sırala.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {reorderDirty ? (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
              Kaydedilmemiş değişiklik
            </span>
          ) : (
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
              Senkron
            </span>
          )}
          <button
            type="button"
            onClick={handleSaveReorder}
            disabled={!reorderDirty || savingReorder}
            className="rounded-2xl bg-primary px-4 py-3 text-sm font-black text-white transition hover:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {savingReorder ? 'Kaydediliyor...' : 'Düzeni Kaydet'}
          </button>
        </div>
      </div>

      {featuredPosts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-sm leading-7 text-slate-600 dark:border-slate-700 dark:text-slate-300">
          Henüz featured yazı yok. Yazılar ekranından istediğin yazıları öne çıkar, sonra burada sırala.
        </div>
      ) : (
        <div className="space-y-3">
          {featuredPosts.map((post, index) => (
            <div
              key={post.id}
              draggable
              onDragStart={() => handleDragStart(post.id)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => handleDropOnPost(post.id)}
              onDragEnd={() => setDraggedPostId(null)}
              className={`rounded-[1.5rem] border bg-white p-4 transition dark:bg-slate-950 ${
                draggedPostId === post.id
                  ? 'border-primary shadow-lg shadow-primary/10 dark:border-primary'
                  : 'border-slate-200 dark:border-slate-800'
              }`}
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-black text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    #{index + 1}
                  </div>
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="material-symbols-outlined cursor-grab text-slate-400">drag_indicator</span>
                      <h3 className="font-serif text-xl font-bold tracking-tight text-slate-950 dark:text-white">
                        {post.title}
                      </h3>
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-primary">
                        Featured
                      </span>
                    </div>
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      /blog/{post.slug} • {post.category?.name || 'Kategorisiz'}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleMovePost(post.id, 'up')}
                    className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Yukarı
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMovePost(post.id, 'down')}
                    className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Aşağı
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleFeaturedInBoard(post.id)}
                    className="rounded-xl border border-red-200 px-3 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-700 transition hover:bg-red-50 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/20"
                  >
                    Featured Kaldır
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPlaceholder = (title, description) => (
    <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-sm leading-7 text-slate-600 dark:border-slate-700 dark:text-slate-300">
      <div className="text-lg font-black tracking-tight text-slate-950 dark:text-white">{title}</div>
      <p className="mt-2">{description}</p>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <AdminSidebar mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />

      <main className="min-w-0 flex-1 overflow-x-hidden md:ml-[var(--admin-sidebar-width,18rem)]">
        <div className="mx-auto flex min-w-0 max-w-7xl flex-col gap-6 px-4 py-6 md:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black tracking-tight text-slate-950 dark:text-white">Blog Yönetimi</h1>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                Public blog ve içerik merkezi için ilk CMS çalışma alanı.
              </p>
            </div>
            <button
              type="button"
              className="inline-flex items-center rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold text-slate-800 md:hidden dark:border-slate-700 dark:text-slate-100"
              onClick={() => setMobileSidebarOpen(true)}
            >
              Menü
            </button>
          </div>

          <div className="grid min-w-0 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
            <aside className="rounded-[1.75rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="space-y-2">
                {BLOG_MENU_ITEMS_V2.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setActiveMenu(item.key)}
                    className={`flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm font-bold transition ${
                      activeMenu === item.key
                        ? 'bg-primary text-white'
                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </aside>

            <section className="min-w-0 space-y-6">
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {[
                  { label: 'Kategori', value: activeCategories.length },
                  { label: 'Yayınlanan', value: stats.publishedCount },
                  { label: 'Taslak', value: stats.draftCount },
                  { label: 'Arşiv', value: stats.archivedCount }
                ].map((card) => (
                  <div
                    key={card.label}
                    className="rounded-[1.5rem] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950"
                  >
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      {card.label}
                    </div>
                    <div className="mt-3 text-3xl font-black tracking-tight text-slate-950 dark:text-white">
                      {card.value}
                    </div>
                  </div>
                ))}
              </div>

              {loading ? (
                <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
                  Blog admin yükleniyor...
                </div>
              ) : error ? (
                <div className="rounded-[1.75rem] border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300">
                  {error}
                </div>
              ) : (
                <>
                  {activeMenu === 'categories' && renderCategoryList()}

                  {activeMenu === 'new-category' && (
                    <CategoryForm
                      title={editingCategoryId ? 'Kategori Düzenle' : 'Yeni Kategori'}
                      submitLabel={editingCategoryId ? 'Kategori Güncelle' : 'Kategori Oluştur'}
                      form={categoryForm}
                      saving={savingCategory}
                      error={categoryFormError}
                    onChange={handleCategoryFieldChange}
                    onAutoSlug={handleAutoCategorySlug}
                    onSubmit={handleSubmitCategory}
                      onCancel={editingCategoryId ? resetCategoryEditor : null}
                      onDelete={editingCategoryId ? handleDeleteCategoryFromEditor : null}
                      deleting={actionLoadingId === editingCategoryId}
                    />
                  )}

                  {activeMenu === 'posts' && renderWorkingPostListV2()}

                  {activeMenu === 'featured' && renderFeaturedBoard()}

                  {activeMenu === 'archive' && renderArchiveList()}

                  {activeMenu === 'new-post' && (
                    <PostEditor
                      title={editingPostId ? 'Yazı Düzenle' : 'Yeni Yazı'}
                      submitLabel={editingPostId ? 'Yazıyı Güncelle' : 'Yazıyı Oluştur'}
                      form={postForm}
                      categories={postAssignableCategories}
                      saving={savingPost}
                      uploadingCoverImage={uploadingCoverImage}
                      uploadingOgImage={uploadingOgImage}
                      error={postFormError}
                      onChange={handlePostFieldChange}
                      onUploadCoverImage={handleUploadCoverImage}
                      onUploadOgImage={handleUploadOgImage}
                      onAutoSlug={handleAutoPostSlug}
                      onSubmit={handleSubmitPost}
                      onCancel={editingPostId ? resetPostEditor : null}
                      onDelete={editingPostId ? handleDeleteFromEditor : null}
                      deleting={actionLoadingId === `post-${editingPostId}`}
                    />
                  )}

                </>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
